import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet, apiPatch } from '../lib/api';
import {
  PageTitle, Card, EmptyState, Section, Kpi, Pill, Table, Th, Td,
} from '../components/ui-kit';
import { CheckCircle, Clock, ExternalLink, Eye, XCircle } from 'lucide-react';

const API = (import.meta as any).env?.VITE_API_BASE ?? 'https://app-xyqar-api.azurewebsites.net';

interface Application {
  id: string;
  wizard: 'advisor' | 'ambassador';
  email: string | null;
  status: string;
  created_at: string;
  review_url: string;
  payload: Record<string, any>;
  assets?: { field_name: string; storage_url?: string }[];
  transcripts?: { id: string; score_delta: number; duration_s: number; summary?: string }[];
}

const STATUS_COLORS: Record<string, 'gray' | 'amber' | 'green' | 'red'> = {
  submitted:    'amber',
  under_review: 'amber',
  approved:     'green',
  rejected:     'red',
  waitlisted:   'gray',
};

export default function Recruitment() {
  const { getToken } = useXyqarAuth();
  const [items, setItems]         = React.useState<Application[] | null>(null);
  const [selected, setSelected]   = React.useState<Application | null>(null);
  const [filter, setFilter]       = React.useState<'all' | 'advisor' | 'ambassador'>('all');
  const [loading, setLoading]     = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken().catch(() => '');
      const wizard = filter === 'all' ? '' : `?wizard=${filter}`;
      const j = await apiGet<{ items: Application[] }>(
        `/api/v1/admin/recruitment/applications${wizard}&limit=100`,
        token,
      );
      setItems(j.items ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [getToken, filter]);

  React.useEffect(() => { load(); }, [load]);

  const updateStatus = async (appId: string, newStatus: string) => {
    try {
      const token = await getToken().catch(() => '');
      await apiPatch(`/api/v1/admin/recruitment/applications/${appId}/status`, { status: newStatus }, token);
      await load();
      if (selected?.id === appId) {
        setSelected(s => s ? { ...s, status: newStatus } : s);
      }
    } catch {
      // noop
    }
  };

  const advisorCount   = (items ?? []).filter(x => x.wizard === 'advisor').length;
  const ambassadorCount = (items ?? []).filter(x => x.wizard === 'ambassador').length;
  const pendingCount   = (items ?? []).filter(x => ['submitted', 'under_review'].includes(x.status)).length;
  const approvedCount  = (items ?? []).filter(x => x.status === 'approved').length;

  const visible = (items ?? []).filter(x => filter === 'all' || x.wizard === filter);

  return (
    <div>
      <PageTitle kicker="Advisor · Ambassador" title="Recruitment" />

      <Section title="Overview">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Advisors"    value={String(advisorCount)} />
          <Kpi label="Ambassadors" value={String(ambassadorCount)} />
          <Kpi accent="amber" label="Pending review" value={String(pendingCount)} />
          <Kpi accent="green" label="Approved"       value={String(approvedCount)} />
        </div>
      </Section>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4 mt-2">
        {(['all', 'advisor', 'ambassador'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition
              ${filter === f ? 'bg-white text-black' : 'border border-white/20 text-white/60 hover:border-white/40'}`}
          >
            {f}
          </button>
        ))}
      </div>

      <Section title="Applications">
        {loading && <EmptyState title="Loading…" />}
        {!loading && visible.length === 0 && (
          <EmptyState title="No applications yet" hint="Applications from advisor and ambassador candidates will appear here." />
        )}
        {!loading && visible.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>Date</Th>
                <Th>Lane</Th>
                <Th>Email</Th>
                <Th>Track</Th>
                <Th>Profile</Th>
                <Th>Score</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {visible.map(app => {
                const selection = app.payload?.selection ?? {};
                const interview = app.payload?.interview ?? {};
                const score     = interview.score_delta ?? app.transcripts?.[0]?.score_delta;
                return (
                  <tr key={app.id} className="hover:bg-white/[0.02] cursor-pointer" onClick={() => setSelected(app)}>
                    <Td>{new Date(app.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}</Td>
                    <Td><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${app.wizard === 'advisor' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-amber-500/20 text-amber-300'}`}>{app.wizard}</span></Td>
                    <Td>{app.email ?? '—'}</Td>
                    <Td className="max-w-[180px] truncate">{selection.track ?? '—'}</Td>
                    <Td>{(selection.aria_profile ?? '').toUpperCase() || '—'}</Td>
                    <Td>{score != null ? <span className={score >= 20 ? 'text-emerald-400 font-bold' : 'text-white/60'}>+{score}</span> : '—'}</Td>
                    <Td><Pill tone={STATUS_COLORS[app.status] ?? 'gray'}>{app.status}</Pill></Td>
                    <Td>
                      <button onClick={e => { e.stopPropagation(); setSelected(app); }} className="p-1 rounded text-white/40 hover:text-white transition">
                        <Eye className="h-4 w-4" strokeWidth={1.8} />
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Section>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setSelected(null)}>
          <div className="flex-1 bg-black/60 backdrop-blur-sm" />
          <div
            className="w-full max-w-xl bg-[#0a0a0a] border-l border-white/10 overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-white/8 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/35 font-mono">{selected.wizard}</p>
                <h2 className="text-lg font-bold mt-0.5">{selected.email ?? selected.id}</h2>
              </div>
              <button onClick={() => setSelected(null)} className="text-white/40 hover:text-white transition text-lg">×</button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Meta */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-white/35 text-xs mb-1">Application ID</p><p className="font-mono text-xs text-white/70">{selected.id}</p></div>
                <div><p className="text-white/35 text-xs mb-1">Submitted</p><p>{new Date(selected.created_at).toLocaleString('en-GB')}</p></div>
                <div><p className="text-white/35 text-xs mb-1">Track</p><p>{(selected.payload?.selection?.track ?? '—')}</p></div>
                <div><p className="text-white/35 text-xs mb-1">ARIA profile</p><p>{(selected.payload?.selection?.aria_profile ?? '—').toUpperCase()}</p></div>
              </div>

              {/* Bio */}
              {selected.payload?.background?.bio && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/35 font-mono mb-2">Bio</p>
                  <p className="text-sm text-white/70 leading-relaxed bg-white/[0.02] rounded-xl p-4 border border-white/8">{selected.payload.background.bio}</p>
                </div>
              )}

              {/* Photo */}
              {selected.assets?.find(a => a.field_name === 'photo')?.storage_url && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/35 font-mono mb-2">Profile photo</p>
                  <div className="flex items-center gap-3">
                    <img
                      src={selected.assets.find(a => a.field_name === 'photo')!.storage_url!}
                      alt="Profile"
                      className="w-16 h-16 rounded-full object-cover border border-white/15"
                      onError={e => (e.currentTarget.style.display = 'none')}
                    />
                    <a href={selected.assets.find(a => a.field_name === 'photo')!.storage_url!} target="_blank" rel="noopener noreferrer" className="text-xs text-white/50 flex items-center gap-1 hover:text-white transition">
                      Open full <ExternalLink className="h-3 w-3" strokeWidth={1.8} />
                    </a>
                  </div>
                </div>
              )}

              {/* ARIA Transcript */}
              {selected.payload?.interview?.transcript_id && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/35 font-mono mb-2">ARIA interview</p>
                  <div className="bg-white/[0.02] rounded-xl p-4 border border-white/8 space-y-2">
                    <p className="text-xs text-white/50 font-mono">transcript: {selected.payload.interview.transcript_id}</p>
                    {selected.payload.interview.score_delta != null && (
                      <p className="text-sm"><span className="text-white/40">Score delta:</span> <span className={`font-bold ${selected.payload.interview.score_delta >= 20 ? 'text-emerald-400' : 'text-white/70'}`}>+{selected.payload.interview.score_delta}</span></p>
                    )}
                  </div>
                </div>
              )}

              {/* Review link */}
              <a
                href={selected.review_url}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition"
              >
                <ExternalLink className="h-4 w-4" strokeWidth={1.8} />
                Open in admin portal
              </a>

              {/* Status actions */}
              <div>
                <p className="text-xs uppercase tracking-widest text-white/35 font-mono mb-3">Update status</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { s: 'under_review', label: 'Under review', icon: Clock },
                    { s: 'approved',     label: 'Approve',      icon: CheckCircle },
                    { s: 'rejected',     label: 'Reject',       icon: XCircle },
                    { s: 'waitlisted',   label: 'Waitlist',     icon: Clock },
                  ].map(({ s, label, icon: Icon }) => (
                    <button
                      key={s}
                      disabled={selected.status === s}
                      onClick={() => updateStatus(selected.id, s)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition
                        ${selected.status === s
                          ? 'bg-white/10 text-white/40 cursor-not-allowed'
                          : s === 'approved'
                            ? 'border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10'
                            : s === 'rejected'
                              ? 'border border-rose-500/40 text-rose-400 hover:bg-rose-500/10'
                              : 'border border-white/20 text-white/60 hover:border-white/40'
                        }`}
                    >
                      <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
